import type { TerminalStatus } from '@repo/desktop-bridge';
import { Button } from '@repo/ui/components/button';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { AuthFrame } from '../auth/AuthFrame';
import { paths } from '../paths';
import { TERMINAL_OUT_OF_SERVICE, TILL_OUT_OF_SERVICE } from './till-messages';
import { TillActions, tillScroll } from './TillFrame';

/**
 * What a terminal shows instead of a counter when its own registration is not
 * one it may act on — withdrawn, run out, or never made.
 *
 * ## Why it exists: issue #215
 *
 * A revoked terminal used to open on the sign-on screen and refuse every PIN
 * typed into it. The refusal was correct and its words were even right — *an
 * owner needs to register the device in the back office* — but they arrived
 * after somebody had signed on, and they pointed at a door that was not on the
 * screen showing them. The only way out of a revoked till on the day it was
 * found was DevTools and a hand-written `history.pushState`.
 *
 * So this screen does two things the sign-on screen could not. It says which of
 * the three standings the device is in **before** anybody enters a credential,
 * and it carries the way to the back office, which is the one place a
 * registration can be replaced.
 *
 * ## It offers a door rather than walking through it
 *
 * The alternative was for the terminal to navigate itself to the back-office
 * sign-in the moment it saw a standing it could not sell on. It does not, and
 * that is a decision rather than an omission: these are public-facing gate and
 * outlet terminals, and a screen that replaced itself with a password prompt
 * would strand a cashier in front of a credential they do not have, with
 * nothing left on screen saying why they were sent there. The person who *can*
 * answer that prompt is an owner, who is not standing here at the moment the
 * lease is withdrawn — they are in the back office on another machine, which is
 * where they withdrew it. The screen states the problem to whoever is here and
 * puts the remedy one tap away for whoever can carry it out.
 *
 * ## The shape is the sign-on screen's
 *
 * A **margin** carrying what is going on and a **field** carrying the act, the
 * same composition `StartShift` uses and for the same reason — this is the
 * screen that appears *instead* of it, on the same terminal, and a cashier
 * should read it as the same system saying something different rather than as a
 * different application. Built to the till column of `docs/ui-conventions.md`,
 * "The two scales": the one control on it is `size="touch"`.
 */
export function TerminalOutOfService({
  status,
  terminal,
  switcher,
}: {
  /**
   * Which standing this is. `'live'` is deliberately not in the type — a device
   * that may trade does not render this screen, and making that unrepresentable
   * is cheaper than a runtime guard nobody reads.
   */
  status: Exclude<TerminalStatus, 'live'>;
  /**
   * This device's ticket prefix, stated in the margin as the thing that is out
   * of service. Absent for a device that has never been registered, which has
   * no prefix to state — it is not yet a terminal.
   */
  terminal?: string;
  /**
   * The outlets this machine can still sell for, where it holds more than one
   * registration (#243). Withdrawing Glass Bridge's must not close the gate:
   * the counter offers the Gate's instead of ending in a door to the back
   * office, which stays for whoever can put Glass Bridge back.
   */
  switcher?: ReactNode;
}) {
  const copy = TERMINAL_OUT_OF_SERVICE[status];

  return (
    <>
      {/* The screen's own name, in the frame's upper zone beside the mark — the
          same slot `StartShift` puts *Start a shift* in. */}
      <TillActions label="Out of service" />

      {/* The sign-on screen's grid, unchanged: `md:` and not `lg:`, because the
          terminals run at 1000x625 and Tailwind's `lg` is 1024px, so a
          composition gated on `lg` would stack on the only hardware that
          matters. `my-auto` and not `justify-center`, so that content taller
          than the window gives way at the bottom rather than overflowing past
          the top with nothing to scroll back to. And inside the shared scroll
          region, for the reason `StartShift` gives (#308): the same switch list
          in the same frame outgrows it the same way. */}
      <div className={`flex min-h-0 flex-1 flex-col ${tillScroll}`}>
        <div className="mx-auto my-auto grid w-full max-w-[55rem] gap-x-8 gap-y-10 md:grid-cols-[12rem_minmax(0,1fr)]">
          {/* The margin: what is going on. No border of its own — the datum above
            and below is the page's rule, and a second one here would be the
            boldness spent twice. */}
          <div className="md:col-start-1">
            <h1 className="font-heading text-2xl font-medium tracking-tight text-foreground">
              {copy.heading}
            </h1>

            {/* The promoted line, where the sign-on screen puts *every ticket you
              sell is recorded against you*. It is the consequence rather than
              the cause, because the consequence is what the person standing
              here needs and the cause is in the field beside it. */}
            <p className="mt-4 text-sm text-foreground">{TILL_OUT_OF_SERVICE}</p>

            <div className="mt-8 space-y-4 border-t border-border pt-6">
              {terminal !== undefined && (
                <AuthFrame.Fact label="This terminal">{terminal}</AuthFrame.Fact>
              )}

              <AuthFrame.Fact label="Registration">{copy.registration}</AuthFrame.Fact>
            </div>
          </div>

          {/* The field: what happened, and the way on. */}
          <div className="md:col-start-2">
            {/* **`text-lg`, which is a size up from the same sentence in the back
              office, and that is the till column of "The two scales" rather than
              emphasis.** This is read standing, at arm's length, by somebody who
              has just found a counter that will not open; 14px of body copy is
              the desk density and it is the wrong one here. `max-w-prose` keeps
              the measure readable at 1280 — the line would otherwise run the
              whole field — and `leading-relaxed` is what a larger size wants. */}
            <p className="max-w-prose text-lg leading-relaxed text-foreground">
              {copy.explanation}
            </p>

            {switcher !== undefined && <div className="mt-8 sm:max-w-xs">{switcher}</div>}

            {/* **The whole of what #215 asked for.** The route is `/`, which the
              guard turns into the sign-in for anybody who is not signed in —
              this widens no guard and declares nothing new, it walks the same
              deny-by-default door the shell already opens an unregistered
              device on.

              `nativeButton={false}` because the rendered element is an anchor:
              Base UI otherwise warns that native button semantics have been
              removed, and it is right to — a link announced as a button does
              not answer the space bar. */}
            <Button
              className="mt-8 w-full sm:max-w-xs"
              size="touch"
              // The accent goes to what the person standing here will do. With
              // another outlet to sell for that is the switch above, and the door
              // is for whoever can put this registration back — so it steps down
              // to neutral rather than out-ranking the way on.
              variant={switcher === undefined ? 'default' : 'outline'}
              nativeButton={false}
              render={<Link to={paths.dashboard} />}
            >
              Open the back office
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
